import { useRef, useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

function useAnim(threshold = 0.1) {
  const ref = useRef<HTMLElement | null>(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setV(true); obs.unobserve(el); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, v };
}

const certTypes = [
  { title: '임시 자격증', level: '2025년 8월~', desc: '12주 전 과정 수료 후 본회 자격증 시험에 합격하면 2025년 8월부터 임시자격증이 발급됩니다. 전문가로서 즉시 활동 가능합니다.', icon: 'ri-award-line' },
  { title: '본회 공식 자격증', level: '정식 발급', desc: '임시자격증 이후 정식 절차를 거쳐 (사)대한문신사중앙회 공식 자격증으로 전환됩니다. 법적 공신력을 갖춘 전문가 인증입니다.', icon: 'ri-shield-star-line' },
  { title: '위생교육 이수증', level: '즉시 발급', desc: '문신사 영업 신고에 필수인 위생교육 이수증을 지부에서 바로 발급받을 수 있습니다. 별도 기관 방문 없이 원스톱 처리 가능합니다.', icon: 'ri-heart-pulse-line' },
];

const examSchedule = [
  { quarter: '수료 후', date: '2025.07월 예정', type: '본회 자격증 시험 (필기 + 실기)', status: '예정' },
  { quarter: '시험 합격 후', date: '2025.08월~', type: '임시자격증 발급', status: '발급예정' },
  { quarter: '추후 안내', date: '미정', type: '정식 자격증 전환', status: '추후공지' },
];

export default function CertificationPage() {
  const s1 = useAnim(0.1);
  const s2 = useAnim(0.1);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20">
        <section className="relative h-72 md:h-96 lg:h-[480px] flex items-center justify-center overflow-hidden">
          <img src="https://public.readdy.ai/ai/img_res/a28b4f9194d2c5c7efa7431e96f112e2.jpg" alt="자격 및 인증" className="absolute inset-0 w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative z-10 text-center px-5">
            <div className="page-hero-badge flex items-center justify-center gap-2 mb-3">
              <span className="w-6 h-px bg-[#C9A84C]" />
              <span className="text-[#C9A84C] text-xs font-bold tracking-widest uppercase">Certification</span>
              <span className="w-6 h-px bg-[#C9A84C]" />
            </div>
            <h1 className="page-hero-title text-3xl md:text-4xl lg:text-5xl font-black text-white">자격 및 인증</h1>
          </div>
        </section>

        <div className="bg-[#F4F6FA] border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-5 md:px-6 py-3 flex items-center gap-2 text-xs text-gray-500">
            <i className="ri-home-line" /><span>홈</span>
            <i className="ri-arrow-right-s-line" />
            <span className="text-[#1B3A6B] font-semibold">자격 및 인증</span>
          </div>
        </div>

        <section ref={s1.ref as React.RefObject<HTMLElement>} className="py-14 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-5 md:px-6">
            <div className={`text-center mb-10 md:mb-14 anim-fade-up ${s1.v ? 'is-visible' : ''}`}>
              <span className="inline-block text-xs font-bold tracking-widest text-[#C9A84C] uppercase border border-[#C9A84C]/40 rounded-sm px-3 py-1 mb-4">자격증 안내</span>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-[#1B3A6B] mb-4">본회 공식 자격증 종류</h2>
              <p className="text-gray-500 text-sm max-w-xl mx-auto">대한문신사중앙회에서 발급하는 공식 자격증으로 전문가로서의 공신력을 갖추세요</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-10 md:mb-14">
              {certTypes.map((cert, i) => (
                <div key={cert.title} className={`bg-[#F4F6FA] rounded-xl p-6 md:p-8 border border-gray-100 hover:border-[#1B3A6B]/20 transition-all card-lift anim-scale-up ${s1.v ? 'is-visible' : ''}`} style={{ transitionDelay: s1.v ? `${150 + i * 100}ms` : '0ms' }}>
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1B3A6B]/10 text-[#1B3A6B] mb-5"><i className={`${cert.icon} text-xl`} /></div>
                  <span className="inline-block bg-[#C9A84C] text-white text-xs font-bold px-3 py-1 rounded-sm mb-3">{cert.level}</span>
                  <h3 className="text-base md:text-lg font-black text-[#1B3A6B] mb-3">{cert.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{cert.desc}</p>
                </div>
              ))}
            </div>
            <div className={`bg-[#1B3A6B] rounded-xl p-6 md:p-10 anim-fade-up ${s1.v ? 'is-visible' : ''}`} style={{ transitionDelay: s1.v ? '450ms' : '0ms' }}>
              <h3 className="text-lg md:text-xl font-black text-white mb-2">2025년 자격증 시험 일정</h3>
              <p className="text-white/50 text-sm mb-6">시험 일정은 변경될 수 있으니 공지사항을 확인해 주세요</p>
              <div className="space-y-0">
                {examSchedule.map((exam, i) => (
                  <div key={i} className={`flex flex-wrap items-center gap-3 md:gap-4 py-4 ${i < examSchedule.length - 1 ? 'border-b border-white/10' : ''}`}>
                    <span className="text-white/50 text-sm w-24 md:w-28">{exam.quarter}</span>
                    <span className="text-[#C9A84C] font-bold text-sm">{exam.date}</span>
                    <span className="text-white text-sm flex-1">{exam.type}</span>
                    <span className={`text-xs font-bold px-3 py-1 rounded-sm whitespace-nowrap ${exam.status === '발급예정' ? 'bg-[#C9A84C] text-white' : exam.status === '예정' ? 'bg-white/20 text-white' : 'bg-white/10 text-white/50'}`}>{exam.status}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section ref={s2.ref as React.RefObject<HTMLElement>} className="py-14 md:py-20 bg-[#F4F6FA]">
          <div className="max-w-7xl mx-auto px-5 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
              <div className={`text-center lg:text-left anim-slide-left ${s2.v ? 'is-visible' : ''}`}>
                <span className="inline-block text-xs font-bold tracking-widest text-[#C9A84C] uppercase border border-[#C9A84C]/40 rounded-sm px-3 py-1 mb-6">위생교육</span>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-[#1B3A6B] mb-6">문신사 필수<br /><span className="text-[#C9A84C]">위생교육 프로그램</span></h2>
                <p className="text-gray-600 text-sm leading-relaxed mb-8">문신사로 영업하기 위해서는 보건복지부 지정 위생교육을 반드시 이수해야 합니다. 중구지부에서는 편리하게 위생교육을 이수하고 이수증을 발급받을 수 있습니다.</p>
                <ul className="space-y-4 text-left">
                  {['연 1회 의무 이수 (8시간)', '감염 예방 및 소독 방법 교육', '관련 법규 및 행정 절차 안내', '이수증 즉시 발급 가능'].map((item, i) => (
                    <li key={item} className={`flex items-center gap-3 text-sm text-gray-700 anim-fade-up ${s2.v ? 'is-visible' : ''}`} style={{ transitionDelay: s2.v ? `${150 + i * 80}ms` : '0ms' }}>
                      <i className="ri-check-line text-[#C9A84C]" />{item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className={`rounded-xl overflow-hidden h-64 md:h-80 border border-gray-100 anim-slide-right ${s2.v ? 'is-visible' : ''}`} style={{ transitionDelay: '100ms' }}>
                <img src="https://readdy.ai/api/search-image?query=professional%20hygiene%20education%20class%20for%20beauty%20professionals%2C%20instructor%20teaching%20sanitation%20procedures%2C%20bright%20clean%20modern%20classroom%2C%20professional%20medical%20training%20atmosphere%2C%20white%20walls%20natural%20lighting&width=700&height=500&seq=hygiene_new1&orientation=landscape" alt="위생교육" className="w-full h-full object-cover object-top" />
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
